import { Arr } from '@deqo/foundation';
import { useCombobox, UseComboboxStateChange } from 'downshift';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled, { css } from 'styled-components';
import { toPromise } from '../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../concepts/resources/Icon';
import { LFC } from '../Legacy';
import { Colors } from './Colors';
import { FixedScroller } from './Scrollers';
import { Spinner } from './Spinner';
import { Shadows } from './Standards';
import { toRem } from './Utils';

const XScrollbars = Scrollbars as any;

export type InputAddon = (() => JSX.Element) | string;
export type InputAddonVariant = 'inline' | 'block' | 'off';
export type InputItem = { id: string, label: string };

export interface InputBaseProps<TItem = string> {
    id?: string;
    name?: string;
    type?: string;
    tabIndex?: number;
    label?: string;
    hint?: string;
    message?: string;
    placeholder?: string;
    itemMode?: boolean;
    items?: TItem[];
    onItemAdd?(items: TItem[]);
    onItemRemove?(item: TItem);
    maxLength?: number | string;
    autoFocus?: boolean;
    autoComplete?: string;
    compact?: boolean;
    error?: boolean;
    before?: InputAddon;
    beforeVariant?: InputAddonVariant;
    after?: InputAddon;
    afterVariant?: InputAddonVariant;
}

export type TypeaheadItem = { id: string, label: string, badge?: string, disabled?: boolean, data?: any };
export const TypeaheadItem = (id: string, label: string, badge?: string, disabled?: boolean, data?: any) =>
    ({ id, label, badge, disabled, data }) as TypeaheadItem;

export type TypeaheadSource = (value: string) => TypeaheadItem[] | Promise<TypeaheadItem[]>;
export type TypeaheadCallback = (item: TypeaheadItem) => boolean | void;

const waitFor = (ms: number) => new Promise((rs) => setTimeout(rs, ms));

export const ThrottledTypeaheadSource = (waitMs: number, ts: TypeaheadSource): TypeaheadSource => {
    const state = { count: 0 };
    return (value) => new Promise<TypeaheadItem[]>((rs, rj) => {
        const me = ++state.count;
        void (waitFor(waitMs).then(() => {
            // If we are the latest, call, otherwise reject
            if (me === state.count) {
                toPromise(ts(value)).then(rs).catch(rj);
            }
            else {
                rj(new Error('Throttled'));
            }
        }));
    });
};

export interface TypeaheadInputProps extends InputBaseProps<TypeaheadItem> {
    triggerOnClick?: boolean;
    typeaheadSource: TypeaheadSource;
    onTypeaheadSelect: TypeaheadCallback;
}

export interface MultilineOptions {
    rows?: number;
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
}

export interface ValueInputProps extends InputBaseProps {
    multiline?: boolean | MultilineOptions;
    value: string;
    onChange?: (value: string) => void;
    onApply?: (value: string) => void;
    onCancel?: () => void;
}

const Frame = styled.div`
    position: relative;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: ${Colors.primaryText};
`;

const Hint = styled.span`
    margin-left: auto;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: ${Colors.text};
`;

const Message = styled.p<{ error?: boolean }>`
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: ${p => p.error ? Colors.red600 : Colors.text};
`;

const SingleField = styled.input<{ itemMode?: boolean }>`
    position: relative;
    ${p => p.itemMode ? 'max-width: 100%' : 'width: 100%'};
    z-index: 1;
    border: none;
    padding: .125rem .375rem;
    background: transparent;

    &::placeholder {
        color: ${Colors.gray400};
    }

    &:focus {
        outline: none !important;
    }
`;

const MultiField = styled.textarea<{ resize?: string }>`
    display: block;
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    resize: ${p => p.resize};

    &::placeholder {
        color: ${Colors.gray400};
    }

    &:focus {
        outline: none !important;
    }
`;

const Border = styled.div<{ active?: boolean, error?: boolean }>`
    
`;

const Drawer = styled.div`
    position: absolute;
    top: 100%;
    left: -1rem;
    right: -1rem;
    margin-top: -0.125rem;
    z-index: 2;
    background: ${Colors.gray50};
    border: solid 1px ${Colors.border};
    box-shadow: ${Shadows.lg};
    border-radius: .5rem;
`;

const DrawerItem = styled.div<{ active?: boolean, selected?: boolean, disabled?: boolean }>`
    display: flex;
    align-items: center;
    padding: 0.5rem .75rem;
    font-size: 1rem;
    color: ${Colors.gray700};
    background: ${p => p.active && !p.disabled ? Colors.hover : 'transparent'};
    ${p => p.disabled ? '' : 'cursor: pointer;'};
`;

const Top = styled.div`
    display: flex;
    align-items: center;
`;

const Badge = styled.div`
    margin-left: auto;
    padding: .125rem .25rem;
    border-radius: .25rem;
    font-size: .75rem;
    font-weight: 500;
    color: ${Colors.primaryDark};
    background: ${Colors.l1};
`;

const InnerLayout = styled.div`
    position: relative;
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: .25rem;
    padding: .25rem;
    cursor: text;
`;

const Main = styled.div<{ active?: boolean, compact?: boolean, error?: boolean, readonly?: boolean }>`
    display: flex;
    position: relative;
    min-height: ${p => toRem(p.compact ? 2 : 2.5)};

    ${InnerLayout} {
        min-height: ${p => toRem(p.compact ? 2 : 2.5)};
        padding: ${p => toRem(p.compact ? .125 : .25)};
    }

    border: solid 1px ${p => p.error ? Colors.red600 : Colors.gray300};
    border-radius: .275rem;
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 5%);
    background: white;

    ${p => p.active && css`
        &:after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: .175rem;
            box-shadow: 0 0 0 .125rem ${Colors.primary};
            pointer-events: none;
        }
    `}

    ${p => p.readonly && css`
        background: ${Colors.gray50};
    `}
    
    &:hover {
        border: solid 1px ${p => p.error ? Colors.red600 : Colors.gray400};
    }

    .spinner {
        position: absolute;
        top: .25rem;
        right: .25rem;
        color: ${Colors.primary};
    }
`;

const Bottom = styled.div`
`;

const addonBaseStyle = css`
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    color: ${Colors.text};
`;

const AddonBefore = styled.span<{ compact?: boolean, error?: boolean, variant?: InputAddonVariant }>`
    ${addonBaseStyle};
    padding: 0 0.75rem;
    border-right: solid 1px ${Colors.gray300};
    border-top-left-radius: .275rem;
    border-bottom-left-radius: .275rem;

    ${p => p.variant === 'inline' ? css`
        padding-right: 0;
        border-right: 0;
    ` : css`
        background: ${Colors.gray50};
    `};
`;

const AddonAfter = styled.span<{ compact?: boolean, error?: boolean, variant?: InputAddonVariant }>`
    ${addonBaseStyle};
    padding: 0 0.75rem;
    border-left: solid 1px ${Colors.gray300};
    border-top-right-radius: .275rem;
    border-bottom-right-radius: .275rem;

    ${p => p.variant === 'inline' ? css`
        padding-left: 0;
        border-left: 0;
    ` : css`
        background: ${Colors.gray50};
    `};
`;

const acceptAddon = (addon?: InputAddon, variant?: InputAddonVariant) => {
    if (!addon || variant === 'off') return null;
    if (typeof (addon) !== 'function') return () => <span>{addon}</span>;
    return addon;
};

const ItemRemove = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: .125rem;
    cursor: pointer;

    &:hover, &:active {
        background: ${Colors.active};
    }
`;

const ItemFrame = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: .25rem;
    padding: .25rem .5rem;
    border-radius: .25rem;
    background: ${Colors.hover};
    color: ${Colors.text};
    user-select: none;
    cursor: default;
`;

const Item = React.memo<{ value: string, onRemove: () => void }>(({ value, onRemove }) => {
    return <ItemFrame>
        <span>{value}</span>
        <ItemRemove onClick={() => onRemove()}>
            <Icon src="deqo://icons/hero/x" size="1" />
        </ItemRemove>
    </ItemFrame>;
});

const MainWithTypeahead: LFC<TypeaheadInputProps> = props => {
    const { typeaheadSource, triggerOnClick } = props;

    const before = acceptAddon(props.before, props.beforeVariant);
    const after = acceptAddon(props.after, props.afterVariant);

    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<TypeaheadItem[]>([]);

    const onAddItemOrTypeaheadSelect = useCallback((item: TypeaheadItem) => {
        if (props.itemMode) {
            props.onItemAdd([item]);
            return true;
        }
        else {
            return props.onTypeaheadSelect(item);
        }
    }, [props.onItemAdd, props.onTypeaheadSelect]);

    const onAddRemoveLastItem = useCallback(() => {
        if (props.itemMode && props.items.length > 0) {
            props.onItemRemove(Arr.last(props.items));
        }
    }, [props.itemMode, props.items, props.onItemRemove]);

    const onValueChange = useCallback(async (nextValue: string, force?: boolean) => {
        const ch = ++changeId.current;
        if (force || !!nextValue?.trim()) {
            setLoading(true);
            try {
                const nextResults = await toPromise(typeaheadSource(nextValue));
                if (ch === changeId.current) {
                    setResults(nextResults);
                    setLoading(false);
                }
            }
            catch {
                if (ch === changeId.current) {
                    setLoading(false);
                }
            }
        }
        else {
            setResults([]);
            setLoading(false);
        }
    }, [typeaheadSource]);

    const changeId = useRef(0);
    const onDownshiftValueChange = useCallback(async (e: UseComboboxStateChange<any>) => {
        await onValueChange(e.inputValue);
    }, [onValueChange]);

    const {
        inputValue,
        isOpen,
        openMenu,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        reset,
    } = useCombobox({
        items: results,
        defaultHighlightedIndex: 0,
        itemToString: (x: any) => x?.label,
        onInputValueChange: onDownshiftValueChange,
        onSelectedItemChange: useCallback((changes: UseComboboxStateChange<any>) => {
            if (changes.selectedItem) {
                if (!changes.selectedItem.disabled && onAddItemOrTypeaheadSelect(changes.selectedItem) || changes.selectedItem.disabled) {
                    reset();
                }
            }
        }, [onAddItemOrTypeaheadSelect]),
    });

    // const onFieldKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    //     if (!props.itemMode) return;

    //     if (e.key === 'Enter') {
    //         onApplyItem(e.currentTarget.value, true);
    //     }
    //     else if (e.key === 'Backspace') {
    //         if (e.currentTarget.value === '') {
    //             const { items } = props;
    //             e.preventDefault();
    //             props.onChange(items[items.length - 1]);
    //             props.onItemRemove(items[items.length - 1]);
    //         }
    //     }
    // }, itemDeps);

    const onFieldKeyDown = useCallback((e: any) => {
        const openWithResults = !isOpen || !results.length;
        if (e.key === 'Home' && !openWithResults) {
            e.target.selectionStart = 0;
            if (!e.shiftKey) e.target.selectionEnd = 0;
        }
        else if (e.key === 'End' && !openWithResults) {
            if (!e.shiftKey) e.target.selectionStart = e.target.value.length;
            e.target.selectionEnd = e.target.value.length;
        }
        else if (e.key === 'Backspace' && e.target.value === '') {
            e.preventDefault();
            onAddRemoveLastItem();
        }
    }, [isOpen, results, onAddRemoveLastItem]);

    const onFieldClick = useCallback(() => {
        if (triggerOnClick) {
            onValueChange(inputValue, true);
        }
    }, [triggerOnClick, inputValue]);


    return <Main active={active} compact={props.compact} error={props.error} readonly={props.type === 'readonly'}>
        {before && <AddonBefore compact={props.compact} error={props.error} variant={props.beforeVariant}>{before()}</AddonBefore>}
        <InnerLayout>
            {props.itemMode && props.items?.map(x => <Item key={x.id} value={x.label} onRemove={() => props.onItemRemove(x)} />)}
            <SingleField
                id={props.id}
                name={props.name}
                type={props.type}
                tabIndex={props.tabIndex}
                // compact={props.compact}
                // error={props.error}
                placeholder={props.placeholder}
                autoFocus={props.autoFocus}
                autoComplete={props.autoComplete}
                maxLength={props.maxLength as any}
                itemMode={props.itemMode}
                // value={props.value}
                // flush={isFlush(before, after)}
                {...getInputProps({
                    onFocus: () => setActive(true),
                    onBlur: () => setActive(false),
                    onKeyDown: onFieldKeyDown,
                    onClick: onFieldClick,
                    // onChange: onFieldValueChange,
                })}
            />
        </InnerLayout>
        {after && <AddonAfter compact={props.compact} error={props.error} variant={props.afterVariant}>{after()}</AddonAfter>}
        <div {...getMenuProps()}>
            {isOpen && !!results.length &&
                <Drawer>
                    <FixedScroller maxHeight="17.75">
                        {results.map((s, i) =>
                            <DrawerItem
                                key={s.id}
                                active={i === highlightedIndex}
                                {...getItemProps({ item: s, index: i, disabled: s.disabled })}
                            >
                                {s.label}
                                {s.badge && <Badge>{s.badge}</Badge>}
                            </DrawerItem>
                        )}
                    </FixedScroller>
                </Drawer>
            }
        </div>
        {loading && <Spinner size={2} stroke={4} className="spinner" />}
    </Main>;
};

const selectFieldWithProps = (opts: ValueInputProps): [any, any] => {

    if (opts.multiline) {
        return [MultiField,
            typeof (opts.multiline) === 'object' ? opts.multiline : {}
        ];
    }
    else {
        return [SingleField, {
            type: opts.type,
        }];
    }
};

const MainWithValue: LFC<ValueInputProps> = props => {

    const [Field, fieldProps] = useMemo(() => selectFieldWithProps(props), [props]);

    const before = acceptAddon(props.before, props.beforeVariant);
    const after = acceptAddon(props.after, props.afterVariant);

    const [active, setActive] = useState(false);

    const onApplyItem = (value: string, force?: boolean) => {
        const tokens = value.split(/[\s\,]/).map(x => x.trim());
        if (force || tokens.length >= 2) {
            props.onChange('');
            props.onItemAdd(tokens.map(x => x.trim()).filter(x => !!x));
        }
        else {
            props.onChange(tokens.join(''));
        }
    };

    const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.itemMode) {
            onApplyItem(e.currentTarget.value);
        }
        else {
            props.onChange(e.currentTarget.value);
        }
    };

    const onFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {

        if (e.key === 'Enter') {
            if (props.itemMode) {
                onApplyItem(e.currentTarget.value, true);
            }
            else {
                props.onApply?.(e.currentTarget.value);
            }
        }
        else if (e.key === 'Backspace') {
            if (props.itemMode && e.currentTarget.value === '') {
                const { items } = props;
                e.preventDefault();
                if (!!items.length) {
                    props.onChange(items[items.length - 1]);
                    props.onItemRemove(items[items.length - 1]);
                }
            }
        }
        else if (e.key === 'Escape') {
            if (!props.itemMode) {
                props.onCancel?.();
            }
        }
    };

    return <Main active={active} compact={props.compact} error={props.error} readonly={props.type === 'readonly'} >
        {before && <AddonBefore compact={props.compact} error={props.error} variant={props.beforeVariant}>{before()}</AddonBefore>}
        <InnerLayout>
            {props.itemMode && props.items?.map(x => <Item key={x} value={x} onRemove={() => props.onItemRemove(x)} />)}
            <Field
                {...fieldProps}
                id={props.id}
                name={props.name}
                tabIndex={props.tabIndex}
                placeholder={!props.items?.length ? props.placeholder : null}
                autoFocus={props.autoFocus}
                autoComplete={props.autoComplete}
                maxLength={props.maxLength as any}
                value={props.value}
                onFocus={() => setActive(true)}
                onBlur={() => setActive(false)}
                onChange={onFieldChange}
                onKeyDown={onFieldKeyDown}
            />
        </InnerLayout>
        {after && <AddonAfter compact={props.compact} error={props.error} variant={props.afterVariant}>{after()}</AddonAfter>}
    </Main>;
};
// <Border active={active} compact={props.compact} {...getRootProps()}>
//         <Field {...getInputProps()}
//             ref={fieldRef as any}
//             id={props.id}
//             name={props.name}
//             type={props.type}
//             compact={props.compact}
//             error={props.error}
//             placeholder={props.placeholder}
//             value={props.value}
//             flush={isFlush(before, after)}
//             onChange={onChange}
//             onFocus={onFieldFocus}
//             onBlur={onFieldBlur}
//         />

//         {after && <AddonAfter compact={props.compact} error={props.error} variant={props.afterVariant}>{after()}</AddonAfter>}
//     </Border>

const Base: LFC<TypeaheadInputProps | ValueInputProps> = props => {

    // const onFieldChange = useCallback((e: any) => {
    //     if (onChange) onChange(e.target.value);
    //     if (props.typeahead) void (props.typeahead.onQuery(e.target.value).then(setSuggestions));
    // }, [onChange, typeahead]);

    const frameRef = useRef<HTMLDivElement>(null);

    const onFrameClick = (e: any) => {
        // If target is frame (and not sub-element) focus the input
        if (e.target === frameRef.current) {
            e.target.querySelector('input')?.focus();
        }
    };

    return <Frame onClick={onFrameClick}>
        {(props.label || props.hint) && <Top>
            {props.label && <Label>{props.label}</Label>}
            {props.hint && <Hint>{props.hint}</Hint>}
        </Top>}
        {'typeaheadSource' in props && <MainWithTypeahead {...props} />}
        {!('typeaheadSource' in props) && <MainWithValue {...props} />}
        {props.message && <Bottom>
            <Message error={props.error}>{props.message}</Message>
        </Bottom>}
    </Frame>;
};

export const Input = Object.assign(Base as FC<ValueInputProps>, {
    AddonBefore,
    AddonAfter,
    Top,
    Label,
    Hint,
    Field: SingleField,
    Main,
    Border,
    Frame,
    Message
});

export const TypeaheadInput = Object.assign(Base as FC<TypeaheadInputProps>, {
    AddonBefore,
    AddonAfter,
    Top,
    Label,
    Hint,
    Field: SingleField,
    Main,
    Border,
    Frame,
    Message
});
